import { FabricObject, StaticCanvas } from 'fabric';
import { Polygon } from 'polygon-clipping';

import { Coords, DigitizedAssetText, MediaText } from 'editor/src/store/design/types';

import CustomFabricImage from 'editor/src/fabric/CustomFabricImage';
import FabricPathText from 'editor/src/fabric/FabricPathText';
import { getBrowserFriendlyColor } from 'editor/src/util/color/colorUtils';
import loadImage from 'editor/src/util/loadImage';

import getElementRect from 'editor/src/component/EditorArea/Spread/Page/MediaElement/getElementRect';
import getDigitizedTextPosition from 'editor/src/component/EditorArea/Spread/Page/MediaElement/Text/getDigitizedTextPosition';
import getTextClipPath from 'editor/src/component/EditorArea/Spread/Page/MediaElement/Text/getTextClipPath';
import getTextShadow from 'editor/src/component/EditorArea/Spread/Page/MediaElement/Text/getTextShadow';
import textElementDataToFabricProps from 'editor/src/component/EditorArea/Spread/Page/MediaElement/Text/textElementDataToFabricProps';

import { CanvasRotation } from '../EditorArea/types';

const addText = async (
  fabricCanvas: StaticCanvas,
  text: MediaText,
  elementIndex: number,
  offset: Coords,
  clipPathPolygons: Polygon[],
  contentClipPath: FabricObject | undefined,
  mm2px: (size: number) => number,
  rotation: CanvasRotation,
  digitizedAsset: DigitizedAssetText | undefined,
) => {
  let textObj;
  const textRect = getElementRect(text, offset, rotation, mm2px);
  const clipPath = getTextClipPath(textRect, text.extra.fontSize, clipPathPolygons, contentClipPath);
  const fabricProps = textElementDataToFabricProps(text, elementIndex, mm2px, false, false, text.extra.fontFamily);
  fabricProps.fill = getBrowserFriendlyColor(fabricProps.fill);
  fabricProps.stroke = fabricProps.stroke ? getBrowserFriendlyColor(fabricProps.stroke) : undefined;
  if (!digitizedAsset?.url) {
    textObj = new FabricPathText(text.extra.text, {
      ...fabricProps,
      ...textRect,
      shadow: getTextShadow(text.extra.shadow, text.extra.fontSize, mm2px),
      clipPath,
      objectCaching: false,
      curve: text.extra.curve,
    });
  } else {
    const margin = digitizedAsset.margin || 0;
    let image;
    try {
      image = await loadImage(digitizedAsset.url, 'anonymous', {
        executor: 'addText',
        text,
      });
    } catch {
      return;
    }

    const imageWidth = image.width - margin * 2;
    const imageHeight = image.height - margin * 2;

    const scaleX = textRect.width / imageWidth;
    const scaleY = textRect.height / imageHeight;
    const elementRect = getDigitizedTextPosition({
      originalLeft: textRect.left,
      originalTop: textRect.top,
      scaleX,
      scaleY,
      angle: textRect.angle,
      margin,
    });
    textObj = new CustomFabricImage(image, {
      ...elementRect,
      getCrossOrigin: () => 'anonymous',
      clipPath,
      objectCaching: false,
      evented: false,
      selectable: false,
      scaleX,
      scaleY,
      angle: textRect.angle,
      zIndex: fabricProps.zIndex,
    });
  }

  fabricCanvas.add(textObj);
};

export default addText;
